import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
  Home, 
  Briefcase, 
  Calendar, 
  Package, 
  Users, 
  Settings, 
  UserCheck, 
  FileText, 
  Receipt,
  ChevronLeft,
  ChevronRight,
  User,
  Menu,
  ChevronDown,
  ChevronUp,
  BarChart3,
  X
} from 'lucide-react';
import { useSidebar } from '../contexts/SidebarContext';
import { useTeam } from '../contexts/TeamContext';
import { useAuth } from '../contexts/AuthContext';

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
  defaultExpanded?: boolean;
}

interface NavigationItem {
  to: string;
  icon: React.ComponentType<any>;
  label: string;
  end?: boolean;
  badge?: string | number;
  description?: string;
}

const Sidebar: React.FC = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { currentUser } = useTeam();
  const { user } = useAuth();
  const [expandedGroups, setExpandedGroups] = React.useState<string[]>(['core', 'business']);

  // Fallback: Use auth user if currentUser not loaded yet
  const displayUser = currentUser || (user ? {
    id: user.id,
    name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    email: user.email || '',
    role: (user.user_metadata?.role || 'owner') as any,
    hourlyRate: 0,
    skills: [],
    isActive: true,
    joinedDate: new Date().toISOString()
  } : null);

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev => 
      prev.includes(groupLabel) 
        ? prev.filter(g => g !== groupLabel)
        : [...prev, groupLabel]
    );
  };

  const navigationGroups: NavigationGroup[] = [
    {
      label: 'Core',
      defaultExpanded: true,
      items: [
        { 
          to: '/', 
          icon: Home, 
          label: 'Dashboard', 
          end: true,
          description: 'Overview and today\'s schedule'
        },
        { 
          to: '/projects', 
          icon: Briefcase, 
          label: 'Projects',
          description: 'Manage all projects'
        },
        { 
          to: '/calendar', 
          icon: Calendar, 
          label: 'Schedule',
          description: 'Project calendar view'
        }
      ]
    },
    {
      label: 'Business',
      defaultExpanded: true,
      items: [
        { 
          to: '/quotes', 
          icon: FileText, 
          label: 'Quotes',
          description: 'Project estimates'
        },
        { 
          to: '/invoices', 
          icon: Receipt, 
          label: 'Invoices',
          description: 'Billing and payments'
        },
        { 
          to: '/clients', 
          icon: UserCheck, 
          label: 'Clients',
          description: 'Client management'
        }
      ]
    },
    {
      label: 'Operations',
      defaultExpanded: false,
      items: [
        { 
          to: '/inventory', 
          icon: Package, 
          label: 'Inventory',
          description: 'Materials and stock'
        },
        { 
          to: '/team', 
          icon: Users, 
          label: 'Team',
          description: 'Staff management'
        },
        { 
          to: '/project-history', 
          icon: BarChart3, 
          label: 'Analytics',
          description: 'Performance insights'
        }
      ]
    }
  ];

  // Initialize expanded groups
  React.useEffect(() => {
    const defaultExpanded = navigationGroups
      .filter(group => group.defaultExpanded)
      .map(group => group.label.toLowerCase());
    setExpandedGroups(defaultExpanded);
  }, []);

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`bg-white dark:bg-gray-800 shadow-md transition-all duration-300 ease-in-out flex-col
                        ${isCollapsed ? 'w-16' : 'w-72'} hidden md:flex fixed md:relative h-full z-30`}>
        {/* Header */}
        <div className={`p-4 border-b border-gray-200 dark:border-gray-700 transition-all duration-300
                        ${isCollapsed ? 'px-2' : 'px-4'}`}>
          <div className="flex items-center justify-between">
            {!isCollapsed && (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 relative overflow-hidden rounded-lg flex items-center justify-center shadow-lg">
                  <div className="relative w-full h-full flex items-center justify-center">
                    {/* Dynamic Chevron Logo */}
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-40 transform -translate-y-1/3 -rotate-12 rounded-md shadow-lg"></div>
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-70 transform -rotate-6 rounded-md shadow-lg"></div>
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-100 transform translate-y-1/3 rotate-12 rounded-md shadow-lg"></div>
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Ergo
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">Project Management</p>
                </div>
              </div>
            )}
            
            {isCollapsed && (
              <div className="flex justify-center w-full">
                <div className="w-8 h-8 relative overflow-hidden rounded-md flex items-center justify-center shadow-lg">
                  <div className="relative w-full h-full flex items-center justify-center">
                    {/* Dynamic Chevron Logo */}
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-40 transform -translate-y-1/3 -rotate-12 rounded-md shadow-lg"></div>
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-70 transform -rotate-6 rounded-md shadow-lg"></div>
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-100 transform translate-y-1/3 rotate-12 rounded-md shadow-lg"></div>
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              ) : (
                <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>
        </div>
        
        {/* Navigation */}
        <nav 
          id="main-navigation"
          className="flex-1 p-4 overflow-y-auto"
          role="navigation"
          aria-label="Main navigation"
          tabIndex={-1}
        >
          <div className="space-y-6">
            {navigationGroups.map((group) => (
              <div key={group.label}>
                {!isCollapsed && (
                  <button
                    onClick={() => toggleGroup(group.label.toLowerCase())}
                    className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 hover:text-gray-700 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1"
                    aria-expanded={expandedGroups.includes(group.label.toLowerCase())}
                    aria-controls={`nav-group-${group.label.toLowerCase()}`}
                  >
                    <span>{group.label}</span>
                    {expandedGroups.includes(group.label.toLowerCase()) ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                )}
                
                {(isCollapsed || expandedGroups.includes(group.label.toLowerCase())) && (
                  <ul 
                    id={`nav-group-${group.label.toLowerCase()}`}
                    className="space-y-1"
                    role="group"
                    aria-labelledby={`nav-group-${group.label.toLowerCase()}-label`}
                  >
                    {group.items.map((item) => (
                      <li key={item.to}>
                        <NavLink 
                          to={item.to}
                          end={item.end}
                          className={({ isActive }) => 
                            `flex items-center text-gray-700 dark:text-gray-200 rounded-lg transition-all duration-200 group relative
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                            ${isActive 
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                            }
                            ${isCollapsed ? 'p-2 justify-center' : 'p-3'}`
                          }
                          aria-current={({ isActive }) => isActive ? 'page' : undefined}
                          title={isCollapsed ? `${item.label}${item.description ? ` - ${item.description}` : ''}` : undefined}
                        >
                          <item.icon className={`w-5 h-5 transition-all duration-200 ${isCollapsed ? '' : 'mr-3'}`} />
                          {!isCollapsed && (
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="transition-opacity duration-200">{item.label}</span>
                                {item.badge && (
                                  <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 rounded-full">
                                    {item.badge}
                                  </span>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {item.description}
                                </p>
                              )}
                            </div>
                          )}
                          {isCollapsed && (
                            <div className="absolute left-full ml-2 bg-gray-900 dark:bg-gray-700 text-white text-sm px-3 py-2 rounded-md 
                                          opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50
                                          whitespace-nowrap">
                              <div className="font-medium">{item.label}</div>
                              {item.description && (
                                <div className="text-xs text-gray-300 mt-1">{item.description}</div>
                              )}
                            </div>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </nav>
        
        {/* User Profile Section */}
        {displayUser && (
          <div className={`p-4 border-t border-gray-200 dark:border-gray-700 transition-all duration-300
                          ${isCollapsed ? 'px-2' : 'px-4'}`}>
            <div className={`flex items-center transition-all duration-300 relative group ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-yellow-500 flex items-center justify-center text-white font-semibold shadow-lg">
                  {displayUser.name.charAt(0).toUpperCase()}
                </div>
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800 transition-all duration-200
                                ${displayUser.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              </div>
              
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {displayUser.name}
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                      displayUser.role === 'admin' || displayUser.role === 'owner'
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200'
                        : displayUser.role === 'supervisor'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    }`}>
                      {displayUser.role.charAt(0).toUpperCase() + displayUser.role.slice(1)}
                    </span>
                    <div className={`w-2 h-2 rounded-full ${displayUser.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  </div>
                </div>
              )}
              
              {isCollapsed && (
                <div className="absolute left-full ml-2 bg-gray-900 dark:bg-gray-700 text-white text-sm px-3 py-2 rounded-md 
                              opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50
                              whitespace-nowrap">
                  <div className="font-medium">{displayUser.name}</div>
                  <div className="text-xs text-gray-300 capitalize">{displayUser.role}</div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Settings */}
        <div className={`p-4 border-t border-gray-200 dark:border-gray-700 transition-all duration-300
                        ${isCollapsed ? 'px-2' : 'px-4'}`}>
          <NavLink 
            to="/settings" 
            className={({ isActive }) => 
              `flex items-center text-gray-700 dark:text-gray-200 rounded-lg transition-all duration-200 group relative
              ${isActive 
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }
              ${isCollapsed ? 'p-2 justify-center' : 'p-3'}`
            }
            title={isCollapsed ? 'Settings & Configuration' : undefined}
          >
            <Settings className={`w-5 h-5 transition-all duration-200 ${isCollapsed ? '' : 'mr-3'}`} />
            {!isCollapsed && (
              <div className="flex-1">
                <span className="transition-opacity duration-200">Settings</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Configuration & preferences
                </p>
              </div>
            )}
            {isCollapsed && (
              <div className="absolute left-full ml-2 bg-gray-900 dark:bg-gray-700 text-white text-sm px-3 py-2 rounded-md 
                            opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50
                            whitespace-nowrap">
                <div className="font-medium">Settings</div>
                <div className="text-xs text-gray-300 mt-1">Configuration & preferences</div>
              </div>
            )}
          </NavLink>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <div className={`md:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300
                      ${!isCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
           onClick={toggleSidebar}>
        <aside className={`bg-white dark:bg-gray-800 shadow-md w-80 max-w-[85vw] h-full transform transition-transform duration-300 ease-in-out flex flex-col
                          ${!isCollapsed ? 'translate-x-0' : '-translate-x-full'}`}
               onClick={(e) => e.stopPropagation()}>
          {/* Mobile sidebar header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 relative overflow-hidden rounded-lg flex items-center justify-center shadow-lg">
                  <div className="relative w-full h-full flex items-center justify-center">
                    {/* Dynamic Chevron Logo */}
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-40 transform -translate-y-1/3 -rotate-12 rounded-md shadow-lg"></div>
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-70 transform -rotate-6 rounded-md shadow-lg"></div>
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-100 transform translate-y-1/3 rotate-12 rounded-md shadow-lg"></div>
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Ergo
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">Project Management</p>
                </div>
              </div>
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
          
          {/* Mobile navigation */}
          <nav className="flex-1 p-4 overflow-y-auto min-h-0">
            <div className="space-y-6">
              {navigationGroups.map((group) => (
                <div key={group.label}>
                  <button
                    onClick={() => toggleGroup(group.label.toLowerCase())}
                    className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    <span>{group.label}</span>
                    {expandedGroups.includes(group.label.toLowerCase()) ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                  
                  {expandedGroups.includes(group.label.toLowerCase()) && (
                    <ul className="space-y-1">
                      {group.items.map((item) => (
                        <li key={item.to}>
                          <NavLink 
                            to={item.to}
                            end={item.end}
                            onClick={toggleSidebar}
                            className={({ isActive }) => 
                              `flex items-center p-3 text-gray-700 dark:text-gray-200 rounded-lg transition-all duration-200
                              ${isActive 
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`
                            }
                          >
                            <item.icon className="w-5 h-5 mr-3" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span>{item.label}</span>
                                {item.badge && (
                                  <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 rounded-full">
                                    {item.badge}
                                  </span>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </nav>
          
          {/* Mobile user profile */}
          {displayUser && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-yellow-500 flex items-center justify-center text-white font-semibold shadow-lg">
                    {displayUser.name.charAt(0).toUpperCase()}
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800
                                  ${displayUser.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {displayUser.name}
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                      displayUser.role === 'admin' || displayUser.role === 'owner'
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200'
                        : displayUser.role === 'supervisor'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    }`}>
                      {displayUser.role.charAt(0).toUpperCase() + displayUser.role.slice(1)}
                    </span>
                    <div className={`w-2 h-2 rounded-full ${displayUser.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Mobile settings */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <NavLink 
              to="/settings"
              onClick={toggleSidebar}
              className={({ isActive }) => 
                `flex items-center p-3 text-gray-700 dark:text-gray-200 rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <Settings className="w-5 h-5 mr-3" />
              <div className="flex-1">
                <span>Settings</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Configuration & preferences
                </p>
              </div>
            </NavLink>
          </div>
        </aside>
      </div>
      
      {/* Enhanced Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-lg z-30 safe-bottom border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-5 h-16">
          <NavLink 
            to="/"
            end
            className={({ isActive }) => 
              `flex flex-col items-center justify-center py-2 text-xs transition-colors
              ${isActive 
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`
            }
          >
            <Home className="w-5 h-5 mb-1" />
            <span className="font-medium">Home</span>
          </NavLink>
          
          <NavLink 
            to="/projects"
            className={({ isActive }) => 
              `flex flex-col items-center justify-center py-2 text-xs transition-colors
              ${isActive 
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`
            }
          >
            <Briefcase className="w-5 h-5 mb-1" />
            <span className="font-medium">Projects</span>
          </NavLink>
          
          <NavLink 
            to="/calendar"
            className={({ isActive }) => 
              `flex flex-col items-center justify-center py-2 text-xs transition-colors
              ${isActive 
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`
            }
          >
            <Calendar className="w-5 h-5 mb-1" />
            <span className="font-medium">Schedule</span>
          </NavLink>
          
          <NavLink 
            to="/quotes"
            className={({ isActive }) => 
              `flex flex-col items-center justify-center py-2 text-xs transition-colors
              ${isActive 
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`
            }
          >
            <FileText className="w-5 h-5 mb-1" />
            <span className="font-medium">Quotes</span>
          </NavLink>
          
          <button
            onClick={toggleSidebar}
            className="flex flex-col items-center justify-center py-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <Menu className="w-5 h-5 mb-1" />
            <span className="font-medium">More</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;